import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
// ORIGINAL
// const PRIMARY = {
//   // lighter: '#C8FACD',
//   // light: '#5BE584',
//   // main: '#00AB55',
//   // dark: '#007B55',
//   // darker: '#005249',
//   lighter: '#D1E9FC',
//   light: '#76B0F1',
//   main: '#2065D1',
//   dark: '#103996',
//   darker: '#061B64',
// };

// GRAY
// const PRIMARY = {
//   lighter: '#b9b8b6',
//   light: '#7a7875',
//   main: '#595856',
//   dark: '#393837',
//   darker: '#181817',
// };

// PURPLE
// const PRIMARY = {
//   lighter: '#c39bd3',
//   light: '#af7ac5',
//   main: '#9b59b6',
//   dark: '#884ea0',
//   darker: '#76448a',
// };

// Teal
// const PRIMARY = {
//   lighter: '#66b2b2',
//   light: '#339999',
//   main: '#008080',
//   dark: '#006666',
//   darker: '#004d4d',
// };

// CYAN
// const PRIMARY = {
//   lighter: '#80e5f2',
//   light: '#4dd2e8',
//   main: '#00bcd4',
//   dark: '#0099aa',
//   darker: '#007780',
// };

// DADA Trading System Brand Colors
const PRIMARY = {
  lighter: '#E3F2FD', // Very light blue
  light: '#2196F3',   // Light blue
  main: '#1565C0',    // Main professional blue
  dark: '#0D47A1',    // Deep blue
  darker: '#002984',  // Dark navy blue
  orange: {           // Brand accent colors
    lighter: '#FFF3E0',
    light: '#FFB74D',
    main: '#FF9800',
    dark: '#F57C00'
  }
};
const SECONDARY = {
  lighter: '#FFF3E0', // Light orange
  light: '#FFB74D',   // Medium orange
  main: '#FF9800',    // Main orange
  dark: '#F57C00',    // Deep orange
  darker: '#E65100',  // Dark orange
};
const DASHBOARD_LIGHT = {
  primary: '#E3F2FD',   // Light blue background
  secondary: '#FFF3E0', // Light orange background
  ternary: '#F5F5F5',   // Light neutral
};
const DASHBOARD_DARK = {
  primary: '#0D47A1',   // Deep blue
  secondary: '#102A43', // Dark blue-gray
  ternary: '#1A365D',   // Navy blue
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E8F5E9', // Lighter shade of the target green
  light: '#4CAF50',   // Light shade of the target green
  main: '#00a152',    // Target dark green color
  dark: '#007E40',    // Darker shade of the target green
  darker: '#005F30',  // Darkest shade of the target green
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#FDECEA', // Lighter but still visible red
  light: '#EF5350',   // Medium-light red
  main: '#D32F2F',    // Darker, more visible red
  dark: '#B71C1C',    // Deep red
  darker: '#7A0000',  // Very dark red
};
const REJECTED = {
  lighter: '#F4DFDB',
  light: '#F08435',
  main: '#EF8C3C',
  dark: '#F56B05',
  darker: '#FD6800',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  reject: createGradient(REJECTED.light, REJECTED.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  reject: { ...REJECTED, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', newneutral: '#FAFAFA', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
    dashboard: { ...DASHBOARD_LIGHT },
  }, dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: '#151E31', default: '#151E31', neutral: GREY[800], newneutral: '#1A2538' },
    action: { active: GREY[500], ...COMMON.action },
    dashboard: { ...DASHBOARD_DARK },
  },
};

export default palette;
